import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Send, ChevronDown, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { getConciergeAvailability } from '../services/conciergeAvailability';
import { isConciergeManualMode } from '../components/operations/manualModeFlag';

const API = process.env.REACT_APP_BACKEND_URL;

const MIN_DESCRIPTION = 30;
const MAX_DESCRIPTION = 2000;

const ConciergeIntakePage = () => {
  const navigate = useNavigate();
  const [availChecked, setAvailChecked] = useState(false);
  const [enabled, setEnabled] = useState(true); // optimistic — replaced on mount
  const [moreOpen, setMoreOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: '',
    phone: '',
    description: '',
    event_type: '',
    event_date: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;
    getConciergeAvailability().then((res) => {
      if (cancelled) return;
      setEnabled(!!res?.enabled);
      setAvailChecked(true);
    });
    return () => { cancelled = true; };
  }, []);

  // When flag is off, redirect home gracefully — no broken state.
  useEffect(() => {
    if (availChecked && !enabled) {
      navigate('/', { replace: true });
    }
  }, [availChecked, enabled, navigate]);

  const update = (key) => (e) => {
    const v = e?.target?.value ?? '';
    setForm((prev) => ({ ...prev, [key]: v }));
    if (errors[key]) {
      setErrors((prev) => { const { [key]: _, ...rest } = prev; return rest; });
    }
  };

  const validate = () => {
    const next = {};
    if (!form.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      next.email = 'Please enter a valid email.';
    }
    const desc = form.description.trim();
    if (desc.length < MIN_DESCRIPTION) {
      next.description = `Tell us a little more — at least ${MIN_DESCRIPTION} characters.`;
    } else if (desc.length > MAX_DESCRIPTION) {
      next.description = `Please keep it under ${MAX_DESCRIPTION} characters.`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim(),
        description: form.description.trim(),
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.event_type.trim()) payload.event_type = form.event_type.trim();
      if (form.event_date.trim()) payload.event_date = form.event_date.trim();

      const res = await axios.post(`${API}/api/concierge/intake`, payload);
      const { event_id, share_token, expires_at, expectation_window } = res.data || {};

      // Stash the token locally so a re-visiting customer sees their submission.
      // Phase 8.6B (F1): also persist the intake email so the signup form
      // can pre-fill it (downstream: ConciergeSubmittedPage + AuthPage).
      try {
        localStorage.setItem('celey:concierge:token', share_token);
        localStorage.setItem('celey:concierge:event_id', event_id);
        localStorage.setItem('celey:concierge:email', form.email.trim());
      } catch (_err) {
        /* localStorage may be unavailable in private mode — ignore */
      }

      navigate(`/concierge/submitted?token=${encodeURIComponent(share_token || '')}&event=${encodeURIComponent(event_id || '')}&window=${encodeURIComponent(expectation_window || '1 week')}&expires=${encodeURIComponent(expires_at || '')}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic v2 field errors
        const next = {};
        detail.forEach((d) => {
          const fld = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : null;
          if (fld) next[fld] = d.msg || 'Invalid value';
        });
        setErrors(next);
        toast.error('Please check the form and try again.');
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Could not submit — please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!availChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="concierge-intake-loading">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (!enabled) {
    return null; // useEffect above already navigated away
  }

  // Phase A — Manual Mode gate (ADR-CONCIERGE-PHASE-A). Read once per
  // render; defaults to TRUE so misconfiguration cannot accidentally
  // expose Hybrid-Mode "Brief" / specific-timing language to a new
  // intake submission.
  const manualMode = isConciergeManualMode();

  const descLen = form.description.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/40 to-white py-10 px-4 sm:py-16">
      <div className="max-w-2xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
          data-testid="concierge-intake-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="mb-8" data-testid="concierge-intake-header">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Celey Concierge
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 leading-tight">
            Tell us about your event.
          </h1>
          <p className="mt-3 text-gray-600 max-w-xl">
            {manualMode ? (
              <>Share what you have in mind in your own words — your Concierge will read through what you shared and reach out by email with next steps. No account needed.</>
            ) : (
              <>Share what you have in mind in your own words — we&apos;ll put together your first
                Concierge Brief (vendors, ideas, next steps) for you. No account needed.</>
            )}
          </p>
        </div>

        <Card className="border-gray-200/80">
          <CardContent className="p-5 sm:p-6">
            <form onSubmit={onSubmit} className="space-y-5" data-testid="concierge-intake-form">
              <div>
                <Label htmlFor="ci-email" className="text-sm">Email <span className="text-red-500">*</span></Label>
                <Input
                  id="ci-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={update('email')}
                  className="mt-1"
                  data-testid="concierge-intake-email"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p className="text-xs text-red-600 mt-1" data-testid="concierge-intake-error-email">
                    {errors.email}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {manualMode
                    ? <>We&apos;ll only use this to email you about your event — no marketing.</>
                    : <>We&apos;ll only use this to send your Brief — no marketing.</>}
                </p>
              </div>

              <div>
                <Label htmlFor="ci-description" className="text-sm">
                  What are you planning? <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="ci-description"
                  rows={6}
                  placeholder="e.g. 40th birthday, around 40 guests, casual outdoor vibe, looking at June 2027 in Slidell. Open to ideas on food & music."
                  value={form.description}
                  onChange={update('description')}
                  className="mt-1 resize-y"
                  maxLength={MAX_DESCRIPTION + 50}
                  data-testid="concierge-intake-description"
                  aria-invalid={!!errors.description}
                />
                <div className="flex items-center justify-between mt-1">
                  <p className={`text-xs ${errors.description ? 'text-red-600' : 'text-gray-500'}`} data-testid="concierge-intake-error-description">
                    {errors.description || `Minimum ${MIN_DESCRIPTION} characters.`}
                  </p>
                  <span className={`text-xs ${descLen > MAX_DESCRIPTION ? 'text-red-600' : 'text-gray-400'}`}>
                    {descLen} / {MAX_DESCRIPTION}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setMoreOpen((v) => !v)}
                className="inline-flex items-center text-sm text-emerald-700 hover:text-emerald-800"
                data-testid="concierge-intake-more-toggle"
              >
                {moreOpen ? <ChevronDown className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                More details (optional)
              </button>

              {moreOpen && (
                <div className="space-y-4 pt-2 border-t border-gray-100" data-testid="concierge-intake-more-section">
                  <div>
                    <Label htmlFor="ci-phone" className="text-sm">Phone (optional)</Label>
                    <Input
                      id="ci-phone"
                      type="tel"
                      autoComplete="tel"
                      inputMode="tel"
                      placeholder="(504) 555-1234"
                      value={form.phone}
                      onChange={update('phone')}
                      className="mt-1"
                      data-testid="concierge-intake-phone"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ci-event-type" className="text-sm">Event type (optional)</Label>
                      <Input
                        id="ci-event-type"
                        placeholder="Birthday, Wedding, Reunion…"
                        value={form.event_type}
                        onChange={update('event_type')}
                        className="mt-1"
                        data-testid="concierge-intake-event-type"
                      />
                    </div>
                    <div>
                      <Label htmlFor="ci-event-date" className="text-sm">Date (optional)</Label>
                      <Input
                        id="ci-event-date"
                        type="date"
                        value={form.event_date}
                        onChange={update('event_date')}
                        className="mt-1"
                        data-testid="concierge-intake-event-date"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-emerald-50/70 border border-emerald-100 p-3" data-testid="concierge-intake-expectation">
                <p className="text-sm text-emerald-900 leading-relaxed">
                  {manualMode ? (
                    <>
                      <span className="font-medium">What happens next:</span> we&apos;ll receive your event, your Concierge will read through what you shared, and we&apos;ll be in touch by email — please keep an eye on your inbox.
                    </>
                  ) : (
                    <>
                      <span className="font-medium">What happens next:</span> your first Concierge Brief
                      arrives within <span className="font-medium">about a week</span> for standard requests.
                      We&apos;ll email you when it&apos;s ready.
                    </>
                  )}
                </p>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={submitting}
                data-testid="concierge-intake-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send to Concierge</>
                )}
              </Button>

              <p className="text-xs text-gray-400 text-center">
                No account needed. Free to submit.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ConciergeIntakePage;
